import * as React from "react";
import { useNavigate, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import ClockIcon from "lucide-react/dist/esm/icons/clock.js";
import MessageSquareIcon from "lucide-react/dist/esm/icons/message-square.js";
import SearchIcon from "lucide-react/dist/esm/icons/search.js";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@shared/ui/command";
import { Skeleton } from "@shared/ui/skeleton";
import {
  useSystemShell,
  useVisibleRoutes,
  type NavItem,
} from "@shared/layouts/system-shell";
import { useFeatureEnabled, useSiteFeatureFlags } from "@shared/hooks/use-site-feature-flags";
import { isPathFeatureEnabled } from "@shared/lib/site-features";
import { apiClient } from "@shared/lib/api/client";
import { activeLanguagePrefix } from "@shared/lib/localized-path";
import { useAIFeatureEnabled } from "@shared/hooks/use-paywall";
import {
  canonicalRouteName,
  getCommandKeywords,
  matchesCommandQuery,
} from "./command-keywords";
import { formatRelativeDate } from "./relative-time";

export const GLOBAL_COMMAND_MENU_EVENT = "app:open-command-menu";

const RECENT_STORAGE_KEY = "cmd-menu-recent";
const MAX_RECENT = 5;
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 20;
const SKELETON_ROWS = 4;
const COMMUNITY_GROUP_KEY = "community";
/** The default (empty-query) Community group shows only the first few pages. */
const COMMUNITY_DEFAULT_LIMIT = 5;
/**
 * Routes that are real pages but noise in the palette: `community-search` is a
 * second search box (the palette IS the search), and the moderation queue is
 * staff-only. They stay reachable from the sidebar / by URL.
 */
const PALETTE_HIDDEN_ROUTES: ReadonlySet<string> = new Set([
  "community-search",
  "moderation",
]);

/** Health Assistant conversation search (palette group). */
const CONVERSATION_MIN_CHARS = 2;
const CONVERSATION_LIMIT = 5;
const CONVERSATION_FEATURE_KEY = "health_assistant";
const CONVERSATIONS_PATH = "/health-assistant";

interface GlobalSearchResult {
  type: string;
  id: string;
  title: string;
  subtitle: string;
  url: string;
  score: number;
}

interface GlobalSearchResponse {
  query: string;
  results: GlobalSearchResult[];
}

interface ConversationHit {
  id: string;
  title: string;
  lastMessageAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface ConversationSearchResponse {
  results?: ConversationHit[];
}

interface RecentItem {
  url: string;
  title: string;
}

function readRecent(): RecentItem[] {
  try {
    const raw = localStorage.getItem(RECENT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as RecentItem[]) : [];
  } catch {
    return [];
  }
}

function saveRecent(item: RecentItem): void {
  try {
    const items = readRecent().filter((r) => r.url !== item.url);
    items.unshift(item);
    localStorage.setItem(
      RECENT_STORAGE_KEY,
      JSON.stringify(items.slice(0, MAX_RECENT)),
    );
  } catch {
    // ignore storage errors
  }
}

function localizeHref(href: string, pathname: string): string {
  const prefix = activeLanguagePrefix(pathname);
  if (!prefix || href === "/" || href === prefix || href.startsWith(`${prefix}/`)) {
    return href;
  }
  return `${prefix}${href}`;
}

interface NavEntry {
  item: NavItem;
  label: string;
  groupLabel: string;
  keywords: string[];
}

export function CommandMenu() {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState<GlobalSearchResult[]>([]);
  // The (trimmed) query the server has answered. `searching` is derived from
  // it, so there is no render where a fresh query briefly looks "empty".
  const [settledQuery, setSettledQuery] = React.useState<string | null>(null);
  const [storedRecent, setRecent] = React.useState<RecentItem[]>([]);
  const [conversations, setConversations] = React.useState<ConversationHit[]>([]);
  const shell = useSystemShell();
  // Inside SystemShell the context already holds the flag-filtered nav; on
  // mobile (no shell context) the palette derives the same list itself, so
  // it never offers a disabled feature on either layout.
  const standalonePipeline = useVisibleRoutes({
    user: shell.user,
    withSidebarPreferences: false,
  });
  const hasShellNav = shell.sidebarGroups.length > 0 || shell.standaloneItems.length > 0;
  const sidebarGroups = hasShellNav ? shell.sidebarGroups : standalonePipeline.groups;
  const standaloneItems = hasShellNav
    ? shell.standaloneItems
    : standalonePipeline.standaloneItems;
  const { data: featureFlags } = useSiteFeatureFlags();
  // Conversations only exist where the assistant does (payment path or a
  // validated BYOK key) AND the site feature is on. Hidden — and never
  // fetched — otherwise.
  const { enabled: conversationsEntitled } = useAIFeatureEnabled(
    CONVERSATION_FEATURE_KEY,
  );
  const { enabled: assistantExposed } = useFeatureEnabled(CONVERSATION_FEATURE_KEY);
  const conversationsEnabled = conversationsEntitled && assistantExposed;
  // Server hits and remembered recents are links too: drop any whose feature
  // has been switched off since (the backend already filters search results;
  // this also covers a stale response or a recent entry saved before).
  const recent = React.useMemo(
    () => storedRecent.filter((item) => isPathFeatureEnabled(item.url, featureFlags)),
    [storedRecent, featureFlags],
  );
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation("common");

  const debounceRef = React.useRef<number | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);

  const isMac =
    typeof navigator !== "undefined" &&
    (/mac/i.test(navigator.platform) || /mac/i.test(navigator.userAgent));

  React.useEffect(() => {
    if (open) setRecent(readRecent());
  }, [open]);

  React.useEffect(() => {
    const openMenu = () => setOpen(true);

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only the chord opens the palette: a bare "k" typed into a field (or
      // Shift/Alt+K) must never be hijacked. With the modifier held it fires
      // from anywhere, including a focused textarea/input — that is exactly
      // where a chat user's hands are — and toggles it closed again from the
      // palette's own input.
      if (
        (e.metaKey || e.ctrlKey) &&
        !e.altKey &&
        !e.shiftKey &&
        e.key.toLowerCase() === "k"
      ) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    window.addEventListener(GLOBAL_COMMAND_MENU_EVENT, openMenu);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener(GLOBAL_COMMAND_MENU_EVENT, openMenu);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  React.useEffect(() => {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    abortRef.current?.abort();

    const trimmed = query.trim();
    setResults([]);
    if (!trimmed) {
      setSettledQuery(null);
      return;
    }

    setSettledQuery(null);
    const controller = new AbortController();
    abortRef.current = controller;

    debounceRef.current = window.setTimeout(async () => {
      const lang = (i18n.language?.split("-")[0] ?? "en") || "en";
      try {
        const response = await apiClient.get<GlobalSearchResponse>(
          `/api/v1/search/?q=${encodeURIComponent(trimmed)}&limit=${SEARCH_LIMIT}&language=${lang}`,
          { signal: controller.signal },
        );
        if (!controller.signal.aborted) setResults(response.results ?? []);
      } catch {
        if (!controller.signal.aborted) setResults([]);
      } finally {
        if (!controller.signal.aborted) setSettledQuery(trimmed);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
      controller.abort();
    };
  }, [query, i18n.language]);

  // Health Assistant conversations: debounced, aborted on every keystroke, and
  // silent on failure (a broken conversation search must never break the
  // palette).
  React.useEffect(() => {
    const trimmed = query.trim();
    if (!open || !conversationsEnabled || trimmed.length < CONVERSATION_MIN_CHARS) {
      setConversations([]);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const response = await apiClient.get<ConversationSearchResponse>(
          `/api/health-assistant/conversations/?q=${encodeURIComponent(trimmed)}`,
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        setConversations(
          (response?.results ?? [])
            .filter((hit) => hit && typeof hit.id === "string")
            .slice(0, CONVERSATION_LIMIT),
        );
      } catch {
        if (!controller.signal.aborted) setConversations([]);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, open, conversationsEnabled]);

  const handleSelect = (url: string, title: string) => {
    saveRecent({ url, title });
    setOpen(false);
    setQuery("");
    navigateToUrl(url);
  };

  // `to` is a path, not a URL: a "?c=<id>" or "#hash" left inside it would be
  // percent-encoded into the path. Split it so query and hash stay real.
  const navigateToUrl = (url: string) => {
    const hashIndex = url.indexOf("#");
    const hash = hashIndex >= 0 ? url.slice(hashIndex + 1) : "";
    const beforeHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
    const queryIndex = beforeHash.indexOf("?");
    const rawPath = queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
    const rawQuery = queryIndex >= 0 ? beforeHash.slice(queryIndex + 1) : "";
    const to = localizeHref(rawPath, pathname);
    if (!rawQuery && !hash) {
      navigate({ to: to as never });
      return;
    }
    const search = Object.fromEntries(new URLSearchParams(rawQuery));
    navigate({
      to: to as never,
      ...(rawQuery ? { search: search as never } : {}),
      ...(hash ? { hash } : {}),
    });
  };

  const trimmedQuery = query.trim();
  const searching = trimmedQuery.length > 0 && settledQuery !== trimmedQuery;

  const toEntry = React.useCallback(
    (item: NavItem): NavEntry => {
      const routeName = canonicalRouteName(item.name);
      const label = t(`navigation.routes.${routeName}`, { defaultValue: item.label });
      const groupLabel = t(`navigation.groups.${item.groupKey}`, {
        defaultValue: item.group,
      });
      return {
        item,
        label,
        groupLabel,
        // English label + slug words stay searchable when the UI is translated.
        keywords: [item.label, ...getCommandKeywords(routeName)],
      };
    },
    [t],
  );

  const isPaletteRoute = (item: NavItem) =>
    !PALETTE_HIDDEN_ROUTES.has(canonicalRouteName(item.name));
  const standaloneEntries = React.useMemo(
    () => (standaloneItems ?? []).filter(isPaletteRoute).map(toEntry),
    [standaloneItems, toEntry],
  );
  const groupedEntries = React.useMemo(
    () =>
      sidebarGroups
        .flatMap((g) => g.items)
        .filter(isPaletteRoute)
        .map(toEntry),
    [sidebarGroups, toEntry],
  );

  // The label is matched as a substring; the group name only as a word PREFIX
  // ("comm" finds the Community pages, but no query that merely happens to sit
  // inside the word "Community" drags all of them into unrelated searches).
  const matchesEntry = (entry: NavEntry) =>
    matchesCommandQuery(
      trimmedQuery,
      [entry.label],
      [...entry.keywords, entry.groupLabel, entry.item.group],
    );

  // Default view: Suggested -> Recent -> Navigate -> Community.
  // Searching: one flat Navigate list (standalone items first).
  const suggestedEntries = trimmedQuery ? [] : standaloneEntries;
  const communityEntries = trimmedQuery
    ? []
    : groupedEntries
        .filter((e) => e.item.groupKey === COMMUNITY_GROUP_KEY)
        .slice(0, COMMUNITY_DEFAULT_LIMIT);
  const navigateEntries = trimmedQuery
    ? [...standaloneEntries, ...groupedEntries].filter(matchesEntry)
    : groupedEntries.filter((e) => e.item.groupKey !== COMMUNITY_GROUP_KEY);

  const visibleResults = results.filter((result) =>
    isPathFeatureEnabled(result.url, featureFlags),
  );
  const hasSearchResults = trimmedQuery.length > 0 && visibleResults.length > 0;
  const hasRecent = !trimmedQuery && recent.length > 0;
  const hasConversations =
    conversationsEnabled &&
    trimmedQuery.length >= CONVERSATION_MIN_CHARS &&
    conversations.length > 0;
  const hasAnyItem =
    hasConversations ||
    suggestedEntries.length > 0 ||
    communityEntries.length > 0 ||
    navigateEntries.length > 0 ||
    hasSearchResults ||
    hasRecent;
  const showSeparator =
    hasSearchResults || hasConversations || hasRecent || suggestedEntries.length > 0;

  const shortcutLabel = isMac ? "⌘K" : "Ctrl K";

  const renderNavItem = (entry: NavEntry, prefix: string) => {
    const { item, label, groupLabel, keywords } = entry;
    const Icon = item.icon;
    return (
      <CommandItem
        key={`${prefix}-${item.href}`}
        value={`${prefix}:${item.href}`}
        keywords={[label, ...keywords]}
        onSelect={() => handleSelect(item.href, label)}
      >
        <Icon className="me-2 h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="flex-1 truncate">{label}</span>
        {item.groupKey !== "general" && item.groupKey !== "standalone" && (
          <span className="ms-2 text-xs text-muted-foreground">{groupLabel}</span>
        )}
      </CommandItem>
    );
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) setQuery("");
      }}
      // Items are filtered above (translated label + per-language keywords);
      // cmdk's own text filter would drop server results whose title does not
      // literally contain the query.
      shouldFilter={false}
      title={t("commandMenu.title", "Command menu")}
      description={t(
        "commandMenu.description",
        "Search commands and navigate the application.",
      )}
      closeLabel={t("commandMenu.close", "Close")}
    >
      <CommandInput
        placeholder={t("commandMenu.placeholder", "Search or jump to...")}
        value={query}
        onValueChange={setQuery}
        aria-label={t("commandMenu.openHint", `Open command menu (${shortcutLabel})`)}
      />
      {/* `max-h-[none]` (arbitrary) so tailwind-merge drops CommandList's own
          `max-h-[300px]`; the plain `max-h-none` is not recognised as the same
          group and the list stayed capped at 300px inside the 420px dialog. */}
      <CommandList className="min-h-0 max-h-[none] flex-1 overflow-y-auto">
        {!searching && !hasAnyItem && (
          <CommandEmpty>
            <div role="status" aria-live="polite" className="space-y-1 px-4">
              <p>{t("commandMenu.empty", "No results found.")}</p>
              <p className="text-xs text-muted-foreground">
                {t("commandMenu.emptyHint", "Try 'food log' or 'symptoms'")}
              </p>
            </div>
          </CommandEmpty>
        )}

        {suggestedEntries.length > 0 && (
          <CommandGroup heading={t("commandMenu.suggested", "Suggested")}>
            {suggestedEntries.map((entry) => renderNavItem(entry, "suggested"))}
          </CommandGroup>
        )}

        {hasConversations && (
          <CommandGroup
            heading={t("commandMenu.conversations", "Health Assistant conversations")}
          >
            {conversations.map((hit) => {
              const title =
                hit.title?.trim() ||
                t("commandMenu.untitledConversation", "Untitled conversation");
              const when = formatRelativeDate(
                hit.lastMessageAt ?? hit.updatedAt ?? hit.createdAt,
                i18n.language,
              );
              return (
                <CommandItem
                  key={`conversation-${hit.id}`}
                  value={`conversation:${hit.id}`}
                  keywords={[title]}
                  onSelect={() =>
                    handleSelect(
                      `${CONVERSATIONS_PATH}?c=${encodeURIComponent(hit.id)}`,
                      title,
                    )
                  }
                >
                  <MessageSquareIcon className="me-2 h-4 w-4 shrink-0" aria-hidden="true" />
                  <bdi className="flex-1 truncate">{title}</bdi>
                  {when && (
                    <span className="ms-2 shrink-0 text-xs text-muted-foreground">
                      {when}
                    </span>
                  )}
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {hasSearchResults && (
          <CommandGroup heading={t("commandMenu.search", "Search results")}>
            {visibleResults.map((result) => (
              <CommandItem
                key={`search-${result.type}-${result.id}`}
                value={`search:${result.type}:${result.id}`}
                keywords={[result.title, result.subtitle].filter(Boolean)}
                onSelect={() => handleSelect(result.url, result.title)}
              >
                <SearchIcon className="me-2 h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="flex-1 truncate">{result.title}</span>
                {result.subtitle && (
                  <span className="ms-2 max-w-48 truncate text-xs text-muted-foreground">
                    {result.subtitle}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {hasRecent && (
          <CommandGroup heading={t("commandMenu.recent", "Recent")}>
            {recent.map((item) => (
              <CommandItem
                key={`recent-${item.url}`}
                value={`recent:${item.url}`}
                keywords={[item.title]}
                onSelect={() => handleSelect(item.url, item.title)}
              >
                <ClockIcon className="me-2 h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="flex-1 truncate">{item.title}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {showSeparator && navigateEntries.length > 0 && <CommandSeparator />}

        {navigateEntries.length > 0 && (
          <CommandGroup heading={t("commandMenu.navigate", "Navigate")}>
            {navigateEntries.map((entry) => renderNavItem(entry, "nav"))}
          </CommandGroup>
        )}

        {communityEntries.length > 0 && (
          <CommandGroup
            heading={t("navigation.groups.community", { defaultValue: "Community" })}
          >
            {communityEntries.map((entry) => renderNavItem(entry, "community"))}
          </CommandGroup>
        )}

        {searching && (
          <div
            role="status"
            aria-live="polite"
            data-testid="command-menu-loading"
            className="space-y-1 p-2"
          >
            <span className="sr-only">{t("commandMenu.searching", "Searching...")}</span>
            {Array.from({ length: SKELETON_ROWS }, (_, i) => (
              <Skeleton
                key={i}
                data-testid="command-menu-skeleton-row"
                aria-hidden="true"
                className="h-9 w-full"
              />
            ))}
          </div>
        )}
      </CommandList>
    </CommandDialog>
  );
}

export default CommandMenu;
